"use client";
import clsx from "clsx";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import Card from "../../common/Card/Card";
import IconTile from "../../common/IconTile/IconTile";
import Typography from "../../common/Typography/Typography";
import Radio from "./Radio";
import { useRadioGroup } from "./RadioGroup";
import "../SelectionCard/SelectionCard.css";

export interface RadioCardProps<TValue extends string = string> {
  value: TValue;
  label: ReactNode;
  description?: ReactNode;
  details?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Additional controls beneath the option label, outside its selecting label. */
  children?: ReactNode;
}

/**
 * A radio option drawn as a selectable card, matching `SelectionCard`. Render it inside a
 * `RadioGroup`; the option header selects it and any extra controls sit below.
 */
export default function RadioCard<TValue extends string = string>({
  value,
  label,
  description,
  details,
  icon,
  disabled = false,
  className,
  children,
}: RadioCardProps<TValue>) {
  const group = useRadioGroup();
  const inputId = useId();
  const fieldsRef = useRef<HTMLDivElement>(null);
  const [activation, setActivation] = useState(0);
  useEffect(() => {
    if (!activation) return;
    fieldsRef.current
      ?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled)',
      )
      ?.focus();
  }, [activation]);
  if (!group) throw new Error("RadioCard must be rendered inside RadioGroup.");
  const selected = group.value === value;
  const optionDisabled = group.disabled || disabled;
  return (
    <div
      className={clsx("calmlens-selection-card", className)}
      data-selected={selected}
      data-disabled={optionDisabled || undefined}
    >
      <Card variant="flush" className="calmlens-selection-card-content calmlens-radio-card-content">
        {/* biome-ignore lint/a11y/useSemanticElements: A separate tab stop keeps every card reachable; the native radio below retains form behavior. */}
        <label
          htmlFor={inputId}
          className="calmlens-radio-card-label"
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: This label implements the card's keyboard radio interaction.
          role="radio"
          tabIndex={optionDisabled ? -1 : 0}
          aria-checked={selected}
          aria-disabled={optionDisabled || undefined}
          aria-invalid={group.error || undefined}
          aria-labelledby={`${inputId}-title`}
          aria-describedby={description ? `${inputId}-description` : undefined}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget || optionDisabled) return;
            const input = event.currentTarget.control as HTMLInputElement;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              input.click();
            } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
              event.preventDefault();
              const options = Array.from(
                input
                  .closest("fieldset")!
                  .querySelectorAll<HTMLInputElement>('input[type="radio"]'),
              ).filter((option) => option.name === input.name && !option.matches(":disabled"));
              const direction = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
              const next =
                options[(options.indexOf(input) + direction + options.length) % options.length];
              next.focus();
              next.click();
            }
          }}
        >
          {icon && <IconTile color={selected ? "primary" : "neutral"}>{icon}</IconTile>}
          <span className="calmlens-selection-card-body">
            <Typography id={`${inputId}-title`} as="span" variant="bodySmall" weight="semibold">
              {label}
            </Typography>
            {description && (
              <Typography
                id={`${inputId}-description`}
                as="span"
                variant="bodyExtraSmall"
                tone="muted"
              >
                {description}
              </Typography>
            )}
            {details && <span className="calmlens-selection-card-details">{details}</span>}
          </span>
          <Radio
            id={inputId}
            value={value}
            disabled={optionDisabled}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => {
              if (!optionDisabled && !group.readOnly) setActivation((count) => count + 1);
            }}
            onFocus={(event) => event.currentTarget.labels?.[0]?.focus()}
            aria-labelledby={`${inputId}-title`}
            aria-describedby={description ? `${inputId}-description` : undefined}
          />
        </label>
        {children && (
          <div ref={fieldsRef} className="calmlens-radio-card-fields">
            {children}
          </div>
        )}
      </Card>
    </div>
  );
}
